import React, { useState, useEffect } from 'react';
import { apiCall } from '../lib/api.js';

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const fetchAgents = async () => {
    try {
      const data = await apiCall('/agents');
      setAgents(data.agents || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setError('');
    try {
      await apiCall('/agent/save', { method: 'POST', body: { name: name.trim(), email: email.trim(), active: true } });
      setName(''); setEmail(''); setShowForm(false); setEditing(null);
      fetchAgents();
    } catch (e) { setError(e.message); }
  };

  const handleEdit = (agent) => {
    setEditing(agent.name);
    setName(agent.name);
    setEmail(agent.email || '');
    setShowForm(true);
  };

  const handleDelete = async (agentName) => {
    if (!confirm('Remove ' + agentName + '? This will not delete their evaluations.')) return;
    try {
      await apiCall('/agent/delete', { method: 'POST', body: { name: agentName } });
      fetchAgents();
    } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-medium">Agents</h2>
        <button onClick={() => { setShowForm(true); setEditing(null); setName(''); setEmail(''); }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium flex items-center gap-1.5">
          <i className="ti ti-plus text-sm" aria-hidden="true"></i> Add agent
        </button>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <h3 className="text-sm font-medium mb-3">{editing ? 'Edit agent' : 'Add new agent'}</h3>
          {error && <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Name (as it appears in the CRM)</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Kevin A" disabled={!!editing}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Email (for scorecard notifications)</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="e.g. kevin@sabrent.com"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium">
              {editing ? 'Update' : 'Add agent'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); setError(''); }}
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Agent list */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : agents.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <i className="ti ti-users text-3xl text-gray-300" aria-hidden="true"></i>
          <p className="text-gray-400 mt-2 text-sm">No agents added yet. Click "Add agent" to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {agents.map(agent => (
            <div key={agent.name} className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                {agent.name.split(' ').map(w => w[0]).join('').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{agent.name}</div>
                <div className="text-xs text-gray-400">{agent.email || 'No email set'}</div>
              </div>
              <span className={'px-2.5 py-1 rounded-full text-[11px] font-medium ' + (agent.active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500')}>
                {agent.active !== false ? 'Active' : 'Inactive'}
              </span>
              <button onClick={() => handleEdit(agent)} className="text-gray-400 hover:text-purple-600 transition" title="Edit">
                <i className="ti ti-pencil text-base" aria-hidden="true"></i>
              </button>
              <button onClick={() => handleDelete(agent.name)} className="text-gray-400 hover:text-red-500 transition" title="Remove">
                <i className="ti ti-trash text-base" aria-hidden="true"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
